import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Paper,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  CircularProgress,
} from "@mui/material";
import { validateCard } from "../../utils/hooks/validateCard";

import CashForm from "./CashForm";
import { useLocation } from "react-router-dom";
import { Stack, useMediaQuery } from "@mui/system";
import formatDate from "../../utils/hooks/formatDate";
import calculateTax, {
  calculateTaxFormatted,
} from "../../utils/hooks/calculateTax";
import { useTranslation } from "react-i18next";
import useTranslatedRooms from "../../utils/rooms";
import {
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js"; // Remove CardElement import
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
function CheckoutForm({ onAmountChange }) {
  const roomsData = useTranslatedRooms();
  const { t, i18n } = useTranslation();

  // TUTTI GLI HOOKS DEVONO ESSERE PRIMA DI QUALSIASI RETURN CONDIZIONALE
  const stripe = useStripe();
  const elements = useElements();
  const location = useLocation();
  const [errors, setErrors] = useState({}); // Stato per gli errori di validazione
  const [paymentMethodUsed, setPaymentMethodUsed] = useState("card");
  const {
    prices,
    dates,
    rooms,
    adultCount,
    childrenCount,
    adultRealCount,
    roomSelections,
  } = location.state || {};
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const totalPrices = prices
    ? prices.reduce((sum, price) => sum + price, 0)
    : 0;
  const selectedRooms = rooms
    ? roomsData.filter((room) => rooms.includes(room.numero))
    : [];
  const isMobile = useMediaQuery("(max-width: 1090px)");
  const [loading, setLoading] = useState(false);
  const [totalPrice, setTotalPrice] = useState(totalPrices);
  const [clientSecret, setClientSecret] = useState("");
  const [cashInfo, setCashInfo] = useState({
    name: "",
    cardNumber: "",
    expiryDate: "",
    cvv: "",
  });
  // Usare i refs per accedere ai valori degli input
  const nameRef = useRef();
  const emailRef = useRef();
  const addressRef = useRef();
  const cityRef = useRef();
  const postalCodeRef = useRef();
  const [phone, setPhone] = useState("");

  // Debug del telefono
  console.log("State telefono corrente:", phone);

  // TUTTI GLI USEEFFECT DEVONO ESSERE DICHIARATI PRIMA DEL RETURN CONDIZIONALE
  useEffect(() => {
    if (!location.state) {
      console.error("Location state is missing.");
      return;
    }

    if (
      !prices ||
      !dates ||
      !rooms ||
      adultCount === undefined ||
      childrenCount === undefined
    ) {
      console.error("Missing necessary booking data in location state.");
      return;
    }

    // Logica per aggiornare il prezzo totale

    if (paymentMethodUsed === "cash") {
      setTotalPrice(totalPrices * 0.9); // Sconto del 10% se si paga in contanti + tassa
      onAmountChange(totalPrices * 0.9);
    } else {
      setTotalPrice(totalPrices); // Prezzo normale con pagamento con carta + tassa
      onAmountChange(totalPrices);
    }
  }, [
    location.state,
    paymentMethodUsed,
    totalPrices,
    adultCount,
    adultRealCount,
    childrenCount,
  ]);
  const hasMounted = useRef(false);

  // Fetch client secret when totalPrice changes
  useEffect(() => {
    if (hasMounted.current) return; // Skip if already mounted

    hasMounted.current = true; // Set it to true on first mount
    const fetchClientSecret = async () => {
      if (totalPrice) {
        try {
          const response = await fetch(
            `https://residenzastorica1811.vercel.app/create-payment-intent`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ amount: Math.round(totalPrice * 100) }), // Convert to cents
            }
          );

          const data = await response.json();

          if (response.ok) {
            setClientSecret(data.clientSecret);
            setPaymentIntentId(data.id);
          } else {
            console.error("Error fetching client secret:", data.error);
          }
        } catch (error) {
          console.error("Error fetching client secret:", error);
        }
      }
    };

    fetchClientSecret();
  }, [totalPrice]);

  async function handleSubmit(event) {
    event.preventDefault();

    // DEBUG - vediamo cosa succede
    console.log("handleSubmit chiamato!");
    console.log("Valore telefono ORIGINALE:", phone);
    console.log("Lunghezza telefono:", phone ? phone.length : 0);

    // Il PhoneInput aggiunge sempre il prefisso, controlliamo se c'è un numero vero
    const phoneWithoutPrefix = phone ? phone.replace(/^\+\d+/, "").trim() : "";
    console.log("Telefono SENZA prefisso:", phoneWithoutPrefix);
    console.log("Lunghezza senza prefisso:", phoneWithoutPrefix.length);

    // Controllo più semplice: se il telefono è più lungo di 4 caratteri dovrebbe essere valido
    const isPhoneValid = phone && phone.length > 4;
    console.log("Telefono valido?", isPhoneValid);

    if (!isPhoneValid) {
      console.log("TELEFONO MANCANTE - BLOCCO QUI!");
      const message = `📞 NUMERO DI TELEFONO RICHIESTO / PHONE NUMBER REQUIRED\n\n🇮🇹 Per favore, aggiungi il tuo numero di telefono per continuare con la prenotazione.\n\n🇺🇸 Please add your phone number to continue with the booking.`;
      alert(message);
      return; // ESCE SUBITO, niente loading
    }

    console.log("Telefono OK, procedo...");

    if (!nameRef.current.value || nameRef.current.value.trim() === "") {
      const message = `👤 NOME RICHIESTO / NAME REQUIRED\n\n🇮🇹 Per favore, inserisci il tuo nome completo per continuare con la prenotazione.\n\n🇺🇸 Please enter your full name to continue with the booking.`;
      alert(message);
      return; // ESCE SUBITO
    }

    if (!emailRef.current.value || emailRef.current.value.trim() === "") {
      const message = `📧 EMAIL RICHIESTA / EMAIL REQUIRED\n\n🇮🇹 Per favore, inserisci il tuo indirizzo email per continuare con la prenotazione.\n\n🇺🇸 Please enter your email address to continue with the booking.`;
      alert(message);
      return; // ESCE SUBITO
    }

    // SOLO SE TUTTO OK, inizia il loading
    setLoading(true);

    const validationErrors = validateCard(cashInfo); // Usa la funzione di validazione
    setErrors(validationErrors); // Aggiorna lo stato degli errori

    const bookingData = {
      name: nameRef.current.value,
      email: emailRef.current.value,
      phone: phone,
      address: addressRef.current.value,
      city: cityRef.current.value,
      postalCode: postalCodeRef.current.value,
      rooms: selectedRooms.map((room) => room.numero),
      adultCount: adultCount,
      childrenCount: childrenCount,
      dates: {
        startDate: new Date(dates.startDate).toISOString(),
        endDate: new Date(dates.endDate).toISOString(),
      },
      totalPrices: paymentMethodUsed === "cash" ? totalPrice : totalPrices,
      paymentMethod: paymentMethodUsed === "cash" ? "cash" : "card",
      cashInfo: cashInfo,
    };
    const tax = calculateTax(adultRealCount, dates.startDate, dates.endDate);
    const serializedRooms = encodeURIComponent(
      JSON.stringify(bookingData.rooms)
    );
    const serializedDates = encodeURIComponent(
      JSON.stringify(bookingData.dates)
    );
    const serializedCashInfo = encodeURIComponent(
      JSON.stringify(bookingData.cashInfo)
    );
    const roomPricePairs = selectedRooms.map((room, index) => ({
      roomNumber: room.numero,
      price: prices[index], // Match price by index
    }));

    // Serialize the room-price pairs
    const serializedRoomPrices = encodeURIComponent(
      JSON.stringify(roomPricePairs)
    );
    const serializedRoomSelections = encodeURIComponent(
      JSON.stringify(roomSelections)
    );

    const return_url = `${
      window.location.origin
    }/payment-result?redirect_status=succeeded&name=${encodeURIComponent(
      bookingData.name
    )}&email=${encodeURIComponent(bookingData.email)}&tax=${encodeURIComponent(
      tax
    )}&phone=${encodeURIComponent(
      bookingData.phone
    )}&rooms=${serializedRooms}&dates=${serializedDates}&paymentMethodUsed=${encodeURIComponent(
      bookingData.paymentMethod
    )}&adultCount=${encodeURIComponent(
      adultCount
    )}&childrenCount=${encodeURIComponent(
      childrenCount
    )}&adultRealCount=${encodeURIComponent(
      adultRealCount
    )}&totalPrices=${encodeURIComponent(
      totalPrice
    )}&cashInfo=${serializedCashInfo}&roomPrices=${serializedRoomPrices}&roomSelections=${serializedRoomSelections}&lang=${encodeURIComponent(
      i18n.language
    )}`;

    if (!stripe || !elements) {
      setLoading(false);
      return;
    }
    try {
      const { error } = await elements.submit(); // Submit the elements first
      if (error) {
        console.error("Error during submitting payment details:", error);
        alert(error.message);
        setLoading(false);
        return;
      }
      if (paymentMethodUsed === "cash") {
        window.location = return_url;
        return;
      }
      if (!paymentIntentId) {
        console.error("PaymentIntent ID is missing.");
        setLoading(false);
        return;
      }
      const { paymentIntent, error: confirmError } =
        await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: return_url,
          },
          clientSecret: clientSecret,
        });

      if (confirmError) {
        setLoading(false);
        console.error("Error during payment confirmation:", confirmError);
        alert(confirmError.message);
        return;
      }
      // Update availability for the booked rooms
    } catch (error) {
      console.error("Error during payment preparation or confirmation:", error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box
      width={"100%"}
      display={"flex"}
      flexDirection={isMobile ? "column-reverse" : "row"}
    >
      <Paper
        elevation={3}
        sx={{
          padding: isMobile ? "20px" : "50px",
          width: isMobile ? "100%" : "50%",
        }}
      >
        <Typography variant="h5" gutterBottom>
          {t("paymentInformation")}
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2} height={"100%"}>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                inputRef={nameRef}
                label={t("fullName")}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                inputRef={emailRef}
                label={t("email")}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  "& .PhoneInput": { width: "100%" },
                  "& .PhoneInputInput": {
                    width: "100%",
                    height: "56px",
                    borderRadius: "4px",
                    border: "1px solid rgba(0,0,0,0.23)",
                    paddingLeft: "12px",
                    paddingRight: "12px",
                    fontSize: "16px",
                  },
                  "& .PhoneInputCountry": {
                    fontSize: isMobile ? "32px" : "16px",
                  },
                  "& .PhoneInputCountryIcon": {
                    width: "24px",
                    height: "24px",
                  },
                  "& .PhoneInputCountrySelect": {
                    fontSize: isMobile ? "24px" : "16px",
                    height: "56px",
                    paddingLeft: "8px",
                    paddingRight: "8px",
                  },
                  "& .PhoneInputCountrySelect option": {
                    fontSize: isMobile ? "24px" : "16px",
                    padding: "8px",
                  },
                  "& select.PhoneInputCountrySelect": {
                    fontSize: isMobile ? "24px" : "16px",
                  },
                }}
              >
                <PhoneInput
                  international
                  defaultCountry={i18n.language === "it" ? "IT" : "US"}
                  value={phone}
                  onChange={(value) => {
                    console.log("PhoneInput onChange:", value);
                    setPhone(value);
                  }}
                  placeholder={t("phone")}
                  countrySelectProps={{
                    style: {
                      fontSize: isMobile ? "18px" : "16px",
                      height: "56px",
                    },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                inputRef={addressRef}
                label={t("address")}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                inputRef={cityRef}
                label={t("city")}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                inputRef={postalCodeRef}
                label={t("postalCode")}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t("paymentMethod")}
              </Typography>
              <RadioGroup
                aria-label={t("paymentMethod")}
                name="paymentMethodUsed"
                value={paymentMethodUsed}
                onChange={(e) => setPaymentMethodUsed(e.target.value)}
              >
                <FormControlLabel
                  value="card"
                  control={<Radio />}
                  label={t("creditCard")}
                />
                <FormControlLabel
                  value="cash"
                  control={<Radio />}
                  label={t("cashDiscount")}
                />
                {paymentMethodUsed === "cash" && (
                  <Typography variant="body2">{t("cashWarning")}</Typography>
                )}
              </RadioGroup>
              <Divider />
            </Grid>
            <Grid
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
              item
              xs={12}
            >
              <Typography variant="caption" color="textSecondary">
                <Typography sx={{ fontSize: "24px", fontFamily: "degular" }}>
                  <span style={{ marginRight: "10px" }}>Total:</span>
                  {totalPrice} €
                </Typography>
                <Typography sx={{ fontSize: "16px", fontFamily: "degular" }}>
                  {t("nonRefundableFee")}
                </Typography>
              </Typography>
            </Grid>
            <Grid item xs={12}>
              {paymentMethodUsed === "cash" ? (
                <CashForm cashInfo={cashInfo} setCashInfo={setCashInfo} />
              ) : (
                <PaymentElement />
              )}
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                disabled={!stripe || loading}
                fullWidth
              >
                {loading ? <CircularProgress size={24} /> : t("pay")}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Stack width={isMobile ? "100%" : "45%"} p={isMobile ? "20px" : 6}>
        <Typography variant="h5" pb={1} gutterBottom borderBottom={1}>
          {t("orderSummary")}
        </Typography>

        <Stack
          justifyContent={"space-between"}
          width={"100%"}
          mt={1}
          spacing={isMobile ? 1 : undefined}
        >
          {selectedRooms && selectedRooms.length > 0 ? (
            selectedRooms.map((room) => (
              <Box
                key={room.numero}
                height={isMobile ? "200px" : undefined}
                p={isMobile ? "5px 20px" : 2}
                backgroundColor={"#f9f9f9"}
                border={1}
                borderColor={"#e0e0e0"}
                borderRadius={1}
                display={"flex"}
                alignItems={"center"}
              >
                <Box
                  key={room.numero}
                  width={"150px"}
                  height={"150px"}
                  display={"flex"}
                >
                  <img
                    src={room.image}
                    alt={room.label}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "center",
                    }}
                  />
                </Box>
                <Stack p={3} width={"80%"}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: "ivymode",
                      fontSize: "16px",
                    }}
                  >
                    {room.name}
                  </Typography>
                  <Typography>{room.label}</Typography>
                  <Typography>
                    €{prices ? prices[selectedRooms.indexOf(room)] : 0}
                  </Typography>
                </Stack>
              </Box>
            ))
          ) : (
            <Typography variant="body1" textAlign="center" p={2}>
              Nessuna stanza selezionata
            </Typography>
          )}
          {dates && dates.startDate && dates.endDate ? (
            <>
              <Typography variant="h6" mt={4}>
                {t("stayDates")}
              </Typography>
              <Box
                sx={{
                  justifyContent: "space-between",
                  width: "100%",
                  display: "flex",
                }}
              >
                <Typography variant="subtitle1">{t("checkIn")}:</Typography>
                <Typography variant="subtitle1">
                  {formatDate(dates.startDate)}
                  <span
                    style={{
                      marginLeft: "5px",
                    }}
                  >
                    {t("checkin")}
                  </span>
                </Typography>
              </Box>
              <Box
                sx={{
                  justifyContent: "space-between",
                  width: "100%",
                  display: "flex",
                }}
              >
                <Typography variant="subtitle1">{t("checkOut")}:</Typography>
                <Typography variant="subtitle1">
                  {formatDate(dates.endDate)}
                  <span
                    style={{
                      marginLeft: "5px",
                    }}
                  >
                    {t("checkout")}
                  </span>
                </Typography>
              </Box>
            </>
          ) : (
            <Typography variant="body1" textAlign="center" p={2}>
              Date non selezionate
            </Typography>
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Typography variant="subtitle1">{t("totalPrice")}:</Typography>
            <Typography variant="subtitle1">€{totalPrice}</Typography>
          </Box>
          {dates &&
          dates.startDate &&
          dates.endDate &&
          adultCount !== undefined &&
          childrenCount !== undefined ? (
            <Typography sx={{ pt: 2 }} variant="subtitle2">
              * {t("tax")} -{" "}
              <strong>
                {" "}
                €
                {calculateTaxFormatted(
                  adultRealCount,
                  dates.startDate,
                  dates.endDate
                )}
              </strong>
            </Typography>
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );
}

export default CheckoutForm;
